import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { QuestAnalyticsService } from '../services/quest-analytics.service';

@Directive({
  selector: '[appQuestAnalytics]',
  standalone: true,
  exportAs: 'appQuestAnalytics',
})
export class MobileQuestAnalyticsDirective implements OnInit, OnDestroy {
  @Input() questId: string;

  private hostElement: HTMLElement;
  private impressionObserver?: IntersectionObserver;
  private hoverTimer?: ReturnType<typeof setTimeout>;
  private impressionTimer?: ReturnType<typeof setTimeout>;

  constructor(
    elementRef: ElementRef<HTMLElement>,
    private questAnalyticsService: QuestAnalyticsService,
  ) {
    this.hostElement = elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.startImpressionObserver();
  }

  ngOnDestroy(): void {
    this.clearHoverTimer();
    this.stopImpressionObserver();
  }

  trackClick(): void {
    if (!this.questId) {
      return;
    }

    this.clearHoverTimer();
    this.stopImpressionObserver();
    this.questAnalyticsService.trackClick(this.questId);
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.questId || this.hoverTimer) {
      return;
    }
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = undefined;
      this.stopImpressionObserver();
      this.questAnalyticsService.trackHover(this.questId);
    }, 300);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.clearHoverTimer();
  }

  private startImpressionObserver(): void {
    if (!this.questId || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.impressionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          this.startImpressionTimer();
          return;
        }

        this.clearImpressionTimer();
      },
      {
        threshold: 0.5,
      },
    );

    this.impressionObserver.observe(this.hostElement);
  }

  private startImpressionTimer(): void {
    if (this.impressionTimer) {
      return;
    }

    this.impressionTimer = setTimeout(() => {
      this.impressionTimer = undefined;
      this.questAnalyticsService.trackImpressionViewed(this.questId);
      this.stopImpressionObserver();
    }, 1000);
  }

  private clearHoverTimer(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }

  private clearImpressionTimer(): void {
    if (this.impressionTimer) {
      clearTimeout(this.impressionTimer);
      this.impressionTimer = undefined;
    }
  }

  private stopImpressionObserver(): void {
    this.clearImpressionTimer();
    if (this.impressionObserver) {
      this.impressionObserver.disconnect();
      this.impressionObserver = undefined;
    }
  }
}
